import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { Box, Button, Stack, TextField, Card, CardContent, Grid, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { get } from 'src/services/httpClient.service';
import { authenticationService } from 'src/services/authentication.service';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import reportDownloadService from 'src/services/reportDownload.service';

interface PackageAgeingRow {
  id: number;
  planName: string;
  range0to30: number;
  range31to60: number;
  range61to90: number;
  range91to120: number;
  above120: number;
}

const PackageWiseAgeingReport: React.FC = () => {
  const [rows, setRows] = useState<PackageAgeingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({ pdf: false, excel: false });
  const [pageSize, setPageSize] = useState(100);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const { vendorId } = authenticationService.getVendorFranchiseIds();
  const [searchText, setSearchText] = useState('');

  const columns: GridColDef[] = [
    { 
      field: 'id', 
      headerName: 'Package ID', 
      flex: 0.5, 
      minWidth: 100,
      type: 'number'
    },
    { 
      field: 'planName', 
      headerName: 'Package Name', 
      flex: 1.5, 
      minWidth: 250 
    },
    { 
      field: 'range0to30', 
      headerName: '0-30 Days', 
      flex: 0.7, 
      minWidth: 100,
      type: 'number',
      headerAlign: 'center',
      align: 'center'
    },
    { 
      field: 'range31to60', 
      headerName: '31-60 Days', 
      flex: 0.7, 
      minWidth: 100,
      type: 'number',
      headerAlign: 'center',
      align: 'center'
    },
    { 
      field: 'range61to90', 
      headerName: '61-90 Days', 
      flex: 0.7, 
      minWidth: 100,
      type: 'number',
      headerAlign: 'center',
      align: 'center'
    },
    { 
      field: 'range91to120', 
      headerName: '91-120 Days', 
      flex: 0.7, 
      minWidth: 100,
      type: 'number',
      headerAlign: 'center',
      align: 'center'
    },
    { 
      field: 'above120', 
      headerName: 'Above 120 Days', 
      flex: 0.7, 
      minWidth: 120,
      type: 'number',
      headerAlign: 'center',
      align: 'center'
    },
    { 
      field: 'total', 
      headerName: 'Total', 
      flex: 0.5, 
      minWidth: 100,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => {
        const row = params.row as PackageAgeingRow;
        return (row.range0to30 || 0) + (row.range31to60 || 0) + 
               (row.range61to90 || 0) + (row.range91to120 || 0) + 
               (row.above120 || 0);
      }
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedDate) return;
      setLoading(true);
      try {
        const date = dayjs(selectedDate).format('YYYY-MM-DD');
        const response = await get(
          `${process.env.REACT_APP_BASE_API_URL}/api/reports/vendor/${vendorId}/getPackageAgeingReport?date=${date}`
        );
        
        const dataWithId = response?.data.map((item: any, index: number) => ({
          id: item.id || index + 1,
          planName: item.planName || '',
          range0to30: item.range0to30 ?? 0,
          range31to60: item.range31to60 ?? 0,
          range61to90: item.range61to90 ?? 0,
          range91to120: item.range91to120 ?? 0,
          above120: item.above120 ?? 0,
        }));
        setRows(dataWithId);
      } catch (error) {
        console.error('Error fetching Package Wise Ageing data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDate, vendorId]);

  const handleDownload = async (format: 'pdf' | 'excel') => {
    if (!selectedDate) {
      alert('Please select a date');
      return;
    }

    setDownloading(prev => ({ ...prev, [format]: true }));
    
    try {
      const dateStr = dayjs(selectedDate).format('YYYY-MM-DD');
      
      const customFileName = `PackageWiseAgeing_Report_${dateStr}_${dayjs().format('HHmmss')}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      const success = await reportDownloadService.downloadReport({
        title: 'Package Wise Ageing Report',
        columns: columns.map(({ field, headerName }) => ({ field, headerName: headerName || '' })),
        rows: rows,
        month: selectedDate.month() + 1,
        year: selectedDate.year(),
        fileName: customFileName,
  
        excelHeaders: [
          "Package ID",
          "Package Name",
          "0-30 Days",
          "31-60 Days",
          "61-90 Days",
          "91-120 Days",
          "Above 120 Days",
          "Total Subscriptions"
        ],
        excelRowMapper: (row: PackageAgeingRow) => [
          row.id || '',
          row.planName || '',
          row.range0to30 ?? 0,
          row.range31to60 ?? 0,
          row.range61to90 ?? 0,
          row.range91to120 ?? 0,
          row.above120 ?? 0,
          (row.range0to30 || 0) + (row.range31to60 || 0) + 
          (row.range61to90 || 0) + (row.range91to120 || 0) + 
          (row.above120 || 0)
        ]
      }, format);
      
      if (!success) {
        alert(`Failed to generate ${format.toUpperCase()}. Please try again.`);
      }
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      alert(`Error generating ${format.toUpperCase()}. Please try again.`);
    } finally {
      setDownloading(prev => ({ ...prev, [format]: false }));
    }
  };

  // Calculate summary statistics
  const getSummaryStats = () => {
    const totalSubscriptions = rows.reduce((sum, row) => 
      sum + (row.range0to30 || 0) + (row.range31to60 || 0) + 
      (row.range61to90 || 0) + (row.range91to120 || 0) + 
      (row.above120 || 0), 0
    );
    
    const totalPackages = rows.length;
    const activePackages = rows.filter(row => 
      (row.range0to30 || 0) + (row.range31to60 || 0) + 
      (row.range61to90 || 0) + (row.range91to120 || 0) + 
      (row.above120 || 0) > 0
    ).length;
    
    return { totalSubscriptions, totalPackages, activePackages };
  };

  const summary = getSummaryStats();

  const filteredRows = rows.filter((row) =>
  row.planName?.toLowerCase().includes(searchText.toLowerCase())
);

  return (
    <>
      <Helmet>
        <title>Package Wise Ageing Report</title>
      </Helmet>

      <PageTitleWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box>
            <PageTitle 
              heading="Package Wise Ageing Report" 
              subHeading="Shows package subscription ageing distribution based on active subscription duration" 
              handleClick={undefined} 
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {hasPermission(["OWNER"]) && (
              <>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<DownloadIcon />} 
                  onClick={() => handleDownload('excel')}
                  disabled={downloading.excel}
                >
                  {downloading.excel ? 'Generating...' : 'Download Excel'}
                </Button>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  startIcon={<PictureAsPdfIcon />} 
                  onClick={() => handleDownload('pdf')}
                  disabled={downloading.pdf}
                >
                  {downloading.pdf ? 'Generating...' : 'Download PDF'}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </PageTitleWrapper>

      {/* Date Filter */}
      <Box sx={{ px: 2, py: 1,display:"flex",gap:2 }}>
       <Box> <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack
            spacing={2}
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
          >
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { minWidth: 200 } 
                } 
              }}
            />
        
          </Stack>
        </LocalizationProvider></Box>
        <Box>
        <TextField
        size="small"
        label="Search Package"
        placeholder="Search by package name"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{
          minWidth: { xs: '100%', sm: 250 }
        }}
      />
      </Box>
      </Box>

      

      {/* Data Grid */}
      <Box sx={{ height: 'calc(100vh - 220px)', width: '100%', padding: '15px' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          getRowHeight={() => 'auto'}
          sx={{
            boxShadow: 'none',
            border: 2,
            borderColor: 'rgb(43 44 45)',
            '& .MuiDataGrid-cell': {
              alignItems: 'start',
              py: 1.5,
            },
          }}
        />
      </Box>
    </>
  );
};

export default PackageWiseAgeingReport;